import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { NavigationStart, Router } from "@angular/router";
import { filter, firstValueFrom } from "rxjs";
import { AppConfig } from "src/app/app.config";
import { AddMenuDialogComponent } from "src/app/modules/employer/add-menu-dialog/add-menu-dialog.component";
import {
  AddMenuDialogInitialMenu,
  canUseAddMenuOption,
} from "src/app/modules/employer/add-menu-dialog/add-menu-dialog-data.model";
import { AuthService } from "src/app/modules/shared/auth/auth.service";
import { MicrofrontendLoaderService } from "../microfrontend-loader/microfrontend-loader.service";
import {
  MICROFRONTEND_ACTION_REQUEST,
  MicrofrontendActionHandler,
  MicrofrontendActionRequestDetail,
  MicrofrontendActionResult,
} from "./microfrontend-actions.model";

/**
 * Runs actions requested by microfrontends (e.g. the dashboard quick actions).
 * The contract is documented in humanage-dashboard `src/app/core/host-actions/acciones-rapidas-host.md`.
 */
@Injectable({
  providedIn: "root",
})
export class MicrofrontendActionsService {
  private readonly elementDefinitionTimeoutMs = 15000;

  private readonly handlers: Record<string, MicrofrontendActionHandler> = {
    "leaves.assign-leave": (payload) =>
      this.openRemoteFlow(
        AppConfig.settings.microfrontends.humanageLeaves,
        "mf-hl-assign-leave",
        payload,
      ),
    "documents.add": () => this.openAddMenu("AddDocumentation"),
    "connect.create-post": () => this.navigate("/employer/humanage-connect"),
    // open the onboarding microfrontend "Alta individual / Alta masiva" dialog
    // once it exposes it. Until then, go to the onboarding page.
    "onboarding.add-candidates": () => this.navigate("/employer/ingresantes"),
  };

  private readonly runningActions = new Map<
    string,
    Promise<MicrofrontendActionResult>
  >();

  constructor(
    private readonly loader: MicrofrontendLoaderService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly authService: AuthService,
  ) {}

  load(): void {
    globalThis.addEventListener(
      MICROFRONTEND_ACTION_REQUEST,
      (event: CustomEvent<MicrofrontendActionRequestDetail>) => {
        const detail = event.detail;
        const handler = this.handlers[detail?.action];

        if (!handler) {
          return;
        }

        event.preventDefault();

        const onSuccess = (result: MicrofrontendActionResult) => {
          if (typeof detail.callbackFn === "function") {
            detail.callbackFn(result);
          }
        };
        const onError = (error: unknown) => {
          console.error(
            `Microfrontend action '${detail.action}' failed`,
            error,
          );
          if (typeof detail.errorFn === "function") {
            detail.errorFn(error);
          }
        };

        this.run(detail.action, () => handler(detail.payload)).then(
          onSuccess,
          onError,
        );
      },
    );
  }

  private run(
    action: string,
    start: () => Promise<MicrofrontendActionResult>,
  ): Promise<MicrofrontendActionResult> {
    let running = this.runningActions.get(action);

    if (!running) {
      running = Promise.resolve()
        .then(start)
        .finally(() => this.runningActions.delete(action));
      this.runningActions.set(action, running);
    }

    return running;
  }

  /** Leaves the current screen. The flow continues in the target section. */
  private async navigate(url: string): Promise<MicrofrontendActionResult> {
    const navigated = await this.router.navigateByUrl(url);

    return { status: navigated ? "completed" : "cancelled", changed: false };
  }

  /** Opens the dialog of the side menu "+" button on one of its options. */
  private async openAddMenu(
    initialMenu: AddMenuDialogInitialMenu,
  ): Promise<MicrofrontendActionResult> {
    if (!canUseAddMenuOption(initialMenu, this.authService)) {
      return { status: "cancelled", changed: false };
    }

    const dialogRef = this.dialog.open(AddMenuDialogComponent, {
      disableClose: true,
      data: { initialMenu },
    });

    const changed = await firstValueFrom(dialogRef.afterClosed());

    return { status: "completed", changed: changed === true };
  }

  /**
   * Mounts a microfrontend element that runs a flow (e.g. a dialog) and
   * resolves when the element emits `closed`, or as cancelled if the user navigates away.
   * `inputs` (the action payload) are set as element properties, i.e. the component inputs.
   */
  private async openRemoteFlow(
    remoteUrl: string,
    elementName: string,
    inputs?: Record<string, unknown>,
  ): Promise<MicrofrontendActionResult> {
    const cancelled: MicrofrontendActionResult = {
      status: "cancelled",
      changed: false,
    };

    let navigated = false;
    let onNavigation = () => {};
    const navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        navigated = true;
        onNavigation();
      });

    try {
      await this.loader.load({
        type: "module",
        remoteEntry: remoteUrl + "remoteEntry.js",
        exposedModule: "./bootstrap",
        elementName,
      });
      await this.whenDefined(elementName);

      if (navigated) {
        return cancelled;
      }

      return await new Promise((resolve) => {
        const element = document.createElement(elementName);
        Object.assign(element, inputs);
        const finish = (result: MicrofrontendActionResult) => {
          element.remove();
          resolve(result);
        };
        onNavigation = () => finish(cancelled);

        element.addEventListener(
          "closed",
          (event: CustomEvent<{ changed?: boolean }>) =>
            finish({ status: "completed", changed: !!event.detail?.changed }),
          { once: true },
        );

        document.body.appendChild(element);
      });
    } finally {
      navigationSubscription.unsubscribe();
    }
  }

  /** An outdated remote may never define the element, so don't wait forever. */
  private async whenDefined(elementName: string): Promise<void> {
    let timeoutId: ReturnType<typeof setTimeout>;

    try {
      await Promise.race([
        customElements.whenDefined(elementName),
        new Promise((_, reject) => {
          timeoutId = setTimeout(
            () =>
              reject(
                new Error(
                  `Microfrontend element '${elementName}' was not defined`,
                ),
              ),
            this.elementDefinitionTimeoutMs,
          );
        }),
      ]);
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
